import { useCallback, useState } from 'react'

import { type GroupContext, type UserContext } from '../types/context'
import type { Initialization } from '@microsoft/applicationinsights-web/types/Initialization'

import { EventQueue } from '../core/EventQueue'
import { enrichEventProperties } from '../utils/enrichment'

export function useEventTracking(
  appInsights: Initialization,
  isEnabled: boolean,
  isDebugMode: boolean,
  userContext: UserContext,
  groupContext: GroupContext,
  enableBatching: boolean,
  batchSize: number,
  batchInterval: number,
  sessionId: React.MutableRefObject<string>
) {
  // Initialize event queue if batching is enabled
  const [eventQueue] = useState(() =>
    enableBatching
      ? new EventQueue(batchSize, batchInterval, (events) => {
          if (!isEnabled) return

          events.forEach((event) => {
            const enrichedProps = enrichEventProperties(event.properties, userContext, groupContext)

            appInsights.trackEvent({ name: event.name, properties: enrichedProps })

            if (isDebugMode) {
              console.group(`🔍 App Insights Event: ${event.name}`)
              console.log('Properties:', enrichedProps)
              console.groupEnd()
            }
          })
        })
      : null
  )

  const capture = useCallback(
    <T extends Record<string, any>>(eventName: string, properties: T) => {
      if (!isEnabled) return

      // Add session ID to all events
      const propsWithSession = {
        ...properties,
        sessionId: sessionId.current
      }

      if (enableBatching && eventQueue) {
        eventQueue.add(eventName, propsWithSession)

        if (isDebugMode) {
          console.log(`🔍 App Insights: Event queued - ${eventName}`, propsWithSession)
        }
      } else {
        const enrichedProps = enrichEventProperties(propsWithSession, userContext, groupContext)
        appInsights.trackEvent({ name: eventName, properties: enrichedProps })

        if (isDebugMode) {
          console.group(`🔍 App Insights Event: ${eventName}`)
          console.log('Properties:', enrichedProps)
          console.groupEnd()
        }
      }
    },
    [isEnabled, enableBatching, eventQueue, isDebugMode, userContext, groupContext, appInsights, sessionId]
  )

  const identify = useCallback(
    (userId: string, email: string, additionalProps: Record<string, any> = {}) => {
      if (!isEnabled) return

      const userProps = {
        userId,
        email,
        ...additionalProps
      }

      // Handle setting user context - this will need to be managed externally
      // as we need to update the provider's state

      appInsights.setAuthenticatedUserContext(userId)

      // Associate current session with user
      appInsights.trackEvent({
        name: 'SessionUserIdentified',
        properties: enrichEventProperties({ sessionId: sessionId.current }, userProps, groupContext)
      })

      if (isDebugMode) {
        console.log('🔍 App Insights: User identified', userProps)
      }

      return userProps
    },
    [appInsights, isEnabled, isDebugMode, groupContext, sessionId]
  )

  return {
    capture,
    identify,
    eventQueue
  }
}
